/**
 * @module KatapultJobUpdateComposers
 * A utility module that contains update composers for the KatapultJob module.
 * These can be used outside of KatapultJob.
 */

import { serverTimestamp } from 'firebase/database';
import { composeJobPermissionsUpdate } from '../UpdateJobPermissions.js';
import {
  getFirebaseFolderPath,
  getItemAttributesRelativePath,
  getItemRelativePath,
  applyItemAttributesMutation,
  rebaseUpdatePaths
} from './KatapultJobUtilities.js';
import { GeofireTools } from '../GeofireTools.js';
import { GoogleGeometry } from '../GoogleGeometry.js';
import { Convert } from '../Convert.js';
import { Round } from '../Round.js';
import { SnapLocationToConnectionPath } from '../SnapLocationToConnectionPath.js';

/**
 * An internal function to compose an update object to update a job property.
 * @param {string} property - The job property to update.
 * @param {any} value - The new value of the property.
 * @param {object} data - The data needed to compose the update object.
 * @param {string} data.jobId - The id of the job.
 * @param {Record<string,string>} data.jobSharing - The current sharing object for the job.
 * @returns {object} The composed update object.
 */
export function composeJobPropertyUpdate(property, value, data) {
  const { jobId, jobSharing } = data;

  // Get all updates to job permissions
  const jobPermissionsData = { [property]: value };
  const companiesToUpdate = Object.keys(jobSharing);

  // Compose the update object
  const update = {
    [`jobs/${jobId}/${property}`]: value,
    ...composeJobPermissionsUpdate(jobId, jobPermissionsData, companiesToUpdate, { updatePath: 'photoheight' })
  };

  return update;
}

/**
 * Compose an update object to update the name of a job.
 * @param {string} name - The new name of the job.
 * @param {object} data - The data needed to compose the update object.
 * @param {string} data.jobId - The id of the job.
 * @param {Record<string,string>} data.jobSharing - The current sharing object for the job.
 * @param {string|null} [data.snapshotParentJobId] - Parent job id whose snapshot reference should be updated.
 * @returns {object} The composed update object.
 */
export function composeJobNameUpdate(name, data) {
  const { jobId, snapshotParentJobId = null } = data;
  return {
    ...composeJobPropertyUpdate('name', name, data),
    ...(snapshotParentJobId && { [`jobs/${snapshotParentJobId}/snapshots/${jobId}/name`]: name })
  };
}

/**
 * Compose an update object to update the "done" state of a job. It's up to you to determine what "done" actually means.
 * @param {boolean} done - The new done status of the job.
 * @param {object} data - The data needed to compose the update object.
 * @param {string} data.jobId - The id of the job.
 * @param {Record<string,string>} data.jobSharing - The current sharing object for the job.
 * @returns {object} The composed update object.
 */
export function composeJobDoneStateUpdate(done, data) {
  return composeJobPropertyUpdate('done', done, data);
}

/**
 * Compose an update object to update the status object of a job.
 * @param {{ published?: boolean, [key: string]: any }} status - The changes to make to the job status object.
 * @param {object} data - The data needed to compose the update object.
 * @param {string} data.jobId - The id of the job.
 * @param {Record<string,string>} data.jobSharing - The current sharing object for the job.
 * @returns {object} The composed update object.
 */
export function composeJobStatusUpdate(status, data) {
  const { jobId, jobSharing } = data;

  // Get the published state and the other status properties
  const { published, ...otherStatusProperties } = status;
  const companiesToUpdate = Object.keys(jobSharing);

  // Compose the update object
  const photoheightUpdate = {
    ...(published !== undefined && {
      [`jobs/${jobId}/status/published`]: published,
      ...composeJobPermissionsUpdate(jobId, { published }, companiesToUpdate, { updatePath: 'photoheight' })
    }),
    ...Object.fromEntries(Object.entries(otherStatusProperties).map(([property, value]) => [`jobs/${jobId}/status/${property}`, value]))
  };

  return photoheightUpdate;
}

/**
 * Compose an update object to update the project folder of a job.
 * @param {string} projectFolder - The new project folder of the job.
 * @param {object} data - The data needed to compose the update object.
 * @param {string} data.jobId - The id of the job.
 * @param {string} data.jobOwner - The id of the job owner.
 * @param {Record<string,string>} [data.jobSharing] - The current sharing object for the job.
 * @param {string} [data.jobProjectFolder] - The current project folder of the job.
 * @param {{ folders?: object, jobs?: object }} [data.ownerCurrentProjectFolderItems] - The items in the project folder that the job is
 *     currently in.
 * @returns {object} The composed update object.
 */
export function composeProjectFolderUpdate(projectFolder, data) {
  const { jobId, jobOwner, jobSharing = {}, jobProjectFolder: currentProjectFolder = null, ownerCurrentProjectFolderItems } = data;
  const siblingFolders = ownerCurrentProjectFolderItems?.folders ?? {};
  const siblingJobs = ownerCurrentProjectFolderItems?.jobs ?? {};

  // Get the new folder path for the job
  const newFolderPath = getFirebaseFolderPath(projectFolder);
  const oldFolderPath = currentProjectFolder !== null ? getFirebaseFolderPath(currentProjectFolder) : null;

  // Determine if the owner's project folder will be empty after the move
  const jobWasLastItemInFolder = Object.keys(siblingFolders).length === 0 && Object.keys(siblingJobs).length === 1;
  const newFolderIsSubfolderOfOldFolder = oldFolderPath !== null && newFolderPath.startsWith(oldFolderPath);
  const oldFolderWillBeEmpty = jobWasLastItemInFolder && !newFolderIsSubfolderOfOldFolder;

  // The old folder shouldn't be deleted if:
  // 1. The old folder path is null, which means the job was never in the folder to begin with
  // 2. ownerCurrentProjectFolderItems is null, which means there's no old folder to begin with
  // 3. The folder path isn't changing (this happens when we're going a true-up for project folders)
  const shouldEvaluateOldFolderForDeletion =
    oldFolderPath !== null && ownerCurrentProjectFolderItems != null && newFolderPath != oldFolderPath;

  // Get the list of companies that will be updated
  const companyIds = Object.keys(jobSharing ?? {});

  // Build the update object
  const photoheightUpdate = {
    // Update the job's project folder
    [`jobs/${jobId}/project_folder`]: projectFolder,
    ...Object.fromEntries(
      // Add the job to the new project folder
      companyIds.map((companyId) => [`company_space/${companyId}/project_folders/${newFolderPath}/jobs/${jobId}`, true])
    ),
    ...(shouldEvaluateOldFolderForDeletion &&
      Object.fromEntries(
        companyIds.map((companyId) =>
          companyId === jobOwner && oldFolderWillBeEmpty
            ? // Prevent the owner's project folder from being deleted if the moved job was the last item in the folder
              [`company_space/${companyId}/project_folders/${oldFolderPath}`, true]
            : // Otherwise, delete the job from the old project folder
              [`company_space/${companyId}/project_folders/${oldFolderPath}/jobs/${jobId}`, null]
        )
      ))
  };

  return photoheightUpdate;
}

/**
 * Compose an update object to update a metadata attribute for a job.
 * @param {string} attribute - The attribute to update.
 * @param {any} value - The new value of the attribute.
 * @param {object} data - The data needed to compose the update object.
 * @param {string} data.jobId - The id of the job.
 * @param {Record<string,string>} data.jobSharing - The current sharing object for the job.
 * @returns {object} The composed update object.
 */
export function composeMetadataAttributeUpdate(attribute, value, data) {
  const { jobId, jobSharing } = data;

  // Get all updates to job permissions
  const jobPermissionsData = { metadata: { [attribute]: value } };
  const companiesToUpdate = Object.keys(jobSharing ?? {});

  // Compose the update object
  const photoheightUpdate = {
    [`jobs/${jobId}/metadata/${attribute}`]: value,
    ...composeJobPermissionsUpdate(jobId, jobPermissionsData, companiesToUpdate, { updatePath: 'photoheight' })
  };

  return photoheightUpdate;
}

/**
 * Compose an update object to update an item's attributes on a job.
 * @param {string} itemType - The type of item to update (one of `'node'`, `'connection'`, or `'section'`).
 * @param {string} itemId - The id of the item to update.
 * @param {object} item - The current item object.
 * @param {import('./KatapultJobUtilities.js').ItemAttributesMutation} attributes - The attributes to update.
 * @param {object} data - The data needed to compose the update object.
 * @param {Record<string, object>} data.jobStyles - The job's styles.
 * @returns {object} The composed update object.
 */
export function composeItemAttributesUpdate(itemType, itemId, item, attributes, data) {
  const { jobStyles } = data;

  // Merge the new attributes with the existing attributes
  const attributesKey = itemType === 'section' ? 'multi_attributes' : 'attributes';
  const updatedItem = { ...item, [attributesKey]: applyItemAttributesMutation(item[attributesKey], attributes) };

  // Compose the update object
  const jobUpdate = {
    // For each attribute, update the attribute instances (or delete the attribute if the value is null)
    ...Object.entries(attributes).reduce(
      (update, [attribute, values]) => ({
        ...update,
        ...(values === null
          ? // If the value is null, delete the attribute
            { [`${getItemAttributesRelativePath(itemId, itemType)}/${attribute}`]: null }
          : // Otherwise, add each instance change to the update object
            Object.fromEntries(
              Object.entries(values).map(([instanceKey, value]) => [
                `${getItemAttributesRelativePath(itemId, itemType)}/${attribute}/${instanceKey}`,
                value
              ])
            ))
      }),
      {}
    ),
    ...composeItemGeoStyleUpdate(itemType, itemId, updatedItem, { jobStyles })
  };

  return jobUpdate;
}

/**
 * Compose an update object to create a node on a job. The node will be added to the job's nodes, and its location will be added to the
 * job's geohash index.
 * @param {string} nodeId - The id of the node to create.
 * @param {object} node - The node object to create. Should include `latitude` and `longitude` properties.
 * @param {object} supplementalData - The supplemental data needed to compose the update object.
 * @param {Record<string, object>} supplementalData.jobStyles - The job's styles.
 * @returns {object} The composed update object.
 */
export function composeNodeCreationUpdate(nodeId, node, supplementalData) {
  const { latitude, longitude } = node;
  const { jobStyles } = supplementalData;

  const jobUpdate = {
    [`nodes/${nodeId}`]: node,
    ...composeGeohashLocationUpdate(nodeId, [latitude, longitude]),
    ...composeItemGeoStyleUpdate('node', nodeId, node, { jobStyles }),
    [`geohash/${nodeId}/t`]: 'n'
    // TODO (2024-04-17): This almost certainly should do more than just these two things (photo association? idk)
  };

  return jobUpdate;
}

/**
 * Compose an update object to update a node's location on a job. Any connected connections, and any sections on those connections, will
 * also be updated.
 * @param {string} nodeId - The id of the node to update.
 * @param {[number, number]} location - The new location of the node.
 * @param {object} data - The data needed to compose the update object.
 * @param {Record<string, object>} data.jobConnections - The job's connections.
 * @param {Record<string, object>} data.jobNodes - The job's nodes.
 * @returns {object} The composed update object.
 */
export function composeNodeLocationUpdate(nodeId, location, data) {
  const { jobConnections, jobNodes } = data;
  const [latitude, longitude] = location;

  // Get a list of all connections that will be modified along with the node
  const connectionsToModify = Object.entries(jobConnections ?? {}).filter(
    ([, connection]) => connection.node_id_1 === nodeId || connection.node_id_2 === nodeId
  );
  // Get a lookup of the connection endpoints that will be modified
  const endpointsByConnectionId = Object.fromEntries(
    connectionsToModify.map(([connectionId, connection]) => [connectionId, connection.node_id_1 === nodeId ? 1 : 2])
  );

  // Build the update object
  const jobUpdate = {
    [`nodes/${nodeId}/latitude`]: latitude,
    [`nodes/${nodeId}/longitude`]: longitude,
    ...composeGeohashLocationUpdate(nodeId, location),
    ...connectionsToModify.reduce(
      (update, [connectionId, connection]) => ({
        ...update,
        ...composeConnectionEndpointUpdate(connectionId, endpointsByConnectionId[connectionId], connection, location, { jobNodes })
      }),
      {}
      // TODO (2024-11-13): If this is the primary of a referenced node pair, update the reference node (?)
    )
  };

  return jobUpdate;
}

/**
 * Compose an update object to update a connection's endpoint location. Any sections on the connection will also be updated.
 * @param {string} connectionId - The id of the connection to update.
 * @param {number} endpointIndex - The index of the endpoint to update (either `1` or `2`).
 * @param {object} connection - The connection object to update.
 * @param {[number, number]} location - The new location of the endpoint.
 * @param {object} data - The data needed to compose the update object.
 * @param {Record<string, Object>} data.jobNodes - The nodes associated with the job.
 * @returns {object} The composed update object.
 */
function composeConnectionEndpointUpdate(connectionId, endpointIndex, connection, location, data) {
  const { jobNodes } = data;
  const adjacentNodeId = connection[`node_id_${endpointIndex === 1 ? 2 : 1}`];
  const adjacentNode = jobNodes[adjacentNodeId];

  // Build the connection's new path from end node 1 to end node 2
  const adjacentNodeLocation = [adjacentNode.latitude, adjacentNode.longitude];
  const [node1Location, node2Location] = endpointIndex === 1 ? [location, adjacentNodeLocation] : [adjacentNodeLocation, location];
  const connectionPathPoints = [node1Location, ...(connection.breakpoints ?? []), node2Location];

  // Calculate the new length for the connection along its whole path
  const connectionPathLatLngs = connectionPathPoints.map(
    ([pointLatitude, pointLongitude]) => new GoogleGeometry.maps.LatLng(pointLatitude, pointLongitude)
  );
  const lengthInMeters = GoogleGeometry.maps.geometry.spherical.computeLength(connectionPathLatLngs);
  const lengthInFeet = Convert(lengthInMeters, 'm', 'ft');
  const lengthInFeetRounded = Round(lengthInFeet, 1);

  // Get all sections that will be modified along with the connection
  const sectionsToModify = Object.entries(connection.sections ?? {});
  // Get the new locations for each section
  const sectionLocationsBySectionKey = Object.fromEntries(
    sectionsToModify.map(([sectionKey, section]) => {
      const { latitude: newLat, longitude: newLng } = SnapLocationToConnectionPath(
        section.latitude,
        section.longitude,
        connectionPathPoints
      );
      return [sectionKey, { latitude: newLat, longitude: newLng }];
    })
  );

  // Build the update object
  const jobUpdate = {
    ...composeGeohashLocationUpdate(`${connectionId}~${endpointIndex}`, location),
    [`geohash/${connectionId}~${endpointIndex == 1 ? 2 : 1}/l2`]: location,
    [`geohash/${connectionId}~1/d`]: lengthInFeetRounded,
    [`geohash/${connectionId}~2/d`]: lengthInFeetRounded,
    ...sectionsToModify.reduce(
      (sectionsUpdate, [sectionKey]) => ({
        ...sectionsUpdate,
        ...composeSectionLocationUpdate(connectionId, sectionKey, [
          sectionLocationsBySectionKey[sectionKey].latitude,
          sectionLocationsBySectionKey[sectionKey].longitude
        ])
      }),
      {}
    )
  };

  return jobUpdate;
}

/**
 * Compose an update object to update a section's location on a connection. Note: this function does not ensure that the new location is
 * valid; the new location should be pre-computed with {@link KatapultGeometry.SnapToLine}.
 * @param {string} connectionId - The id of the connection to update.
 * @param {string} sectionKey - The key of the section to update.
 * @param {[number, number]} location - The new location of the section (should be pre-computed with {@link KatapultGeometry.SnapToLine}).
 * @returns {object} The composed update object.
 */
export function composeSectionLocationUpdate(connectionId, sectionKey, location) {
  const [latitude, longitude] = location;

  // Build the update object
  const jobUpdate = {
    [`connections/${connectionId}/sections/${sectionKey}/latitude`]: latitude,
    [`connections/${connectionId}/sections/${sectionKey}/longitude`]: longitude,
    ...composeGeohashLocationUpdate(`${connectionId}:${sectionKey}`, [latitude, longitude])
  };

  return jobUpdate;
}

/**
 * Compose an update object that moves every section on a connection onto the closest point of the connection's path.
 * @param {string} connectionId - The id of the connection the sections are on.
 * @param {Record<string, object>|null} sections - The sections on the connection, keyed by section key.
 * @param {[number, number][]} connectionPathPoints - The connection's path in order from node 1 to node 2.
 * @returns {object} The composed update object.
 */
export function composeSectionsSnappedToConnectionPathUpdate(connectionId, sections, connectionPathPoints) {
  // Snap each section onto the path and collect its location update
  const jobUpdate = {};
  for (const [sectionKey, section] of Object.entries(sections ?? {})) {
    const snappedSectionLocation = SnapLocationToConnectionPath(section.latitude, section.longitude, connectionPathPoints);
    const snappedSectionCoordinates = [snappedSectionLocation.latitude, snappedSectionLocation.longitude];
    const sectionLocationUpdate = composeSectionLocationUpdate(connectionId, sectionKey, snappedSectionCoordinates);
    Object.assign(jobUpdate, sectionLocationUpdate);
  }

  return jobUpdate;
}

/**
 * Compose an update object to update a geo-location for a geohash.
 * @param {string} geohashKey - The key of the geohash to update.
 * @param {[number, number]} location - The new location of the geohash.
 * @param {[number, number]} [location2] - The location of the second point for a connection
 * @returns {object} The composed update object.
 */
export function composeGeohashLocationUpdate(geohashKey, location, location2) {
  const geohashUpdate = {};
  GeofireTools.updateLocation(geohashKey, location, 10, geohashUpdate);

  // Strip out ".priority" updates from the geohash update
  Object.keys(geohashUpdate).forEach((key) => {
    if (key.includes('.priority')) delete geohashUpdate[key];
  });

  // Connections require this extra piece of data
  if (location2) geohashUpdate[`${geohashKey}/l2`] = location2;

  // Rebase the update object to the `geohash` path
  const jobUpdate = rebaseUpdatePaths('geohash', geohashUpdate);
  return jobUpdate;
}

/**
 * Compose an update object to update a map item's geo-style on a job.
 * @param {string} itemType - The type of item to update (one of `'node'`, `'connection'`, or `'section'`).
 * @param {string} itemId - The id of the item to update.
 * @param {object} item - The current item object.
 * @param {object} data - The data needed to compose the update object.
 * @param {Record<string, object>} data.jobStyles - The job's styles.
 * @returns {object} The composed update object.
 */
export function composeItemGeoStyleUpdate(itemType, itemId, item, data) {
  const { jobStyles } = data;

  // Get the section id if the item is a section
  const [nodeOrConnectionId, sectionKey] = itemId.split(':');

  // Get the job-relative style update for the item (using pass-by-reference)
  const jobUpdate = {};
  const geofireItemType = { node: 'nodes', connection: 'connections', section: 'sections' }[itemType];
  GeofireTools.updateStyle(geofireItemType, nodeOrConnectionId, item, jobUpdate, jobStyles, sectionKey);

  // Strip out ".priority" updates from the update
  Object.keys(jobUpdate).forEach((key) => {
    if (key.includes('.priority')) delete jobUpdate[key];
  });

  return jobUpdate;
}

/**
 * Compose an update object to delete a node from a job. Any connected connections, and any sections on those connections, will also be
 * deleted. Any photos associated with the node, connections, or sections will be unassociated.
 * @param {string} nodeId - The id of the node to delete.
 * @param {object} node - The node object to delete.
 * @param {object} data - The data needed to compose the update object.
 * @param {string} data.jobId - The id of the job.
 * @param {Record<string, Object>} [data.jobConnections = {}] - The connections associated with the job.
 * @param {Record<string, Object>} [data.jobPhotos = {}] - The photos associated with the job.
 * @returns {object} The composed update object.
 */
export function composeNodeDeleteUpdate(nodeId, node, data) {
  let { jobId, jobConnections, jobPhotos } = data;
  jobConnections = jobConnections ?? {};
  jobPhotos = jobPhotos ?? {};

  // Get a list of all connections that will be deleted along with the node
  const connectionsToDelete = Object.entries(jobConnections).filter(
    ([, connection]) => connection.node_id_1 === nodeId || connection.node_id_2 === nodeId
  );

  // Get all photos that will be unassociated as a result of this delete
  const photosToUnassociate = [
    // Get all photos associated with the node
    ...Object.keys(node.photos ?? {}).map((photoId) => ({ photoId, itemId: nodeId, itemType: 'node' })),

    ...connectionsToDelete.flatMap(([connectionId, connection]) => [
      // Get all photos associated with the connection
      ...Object.keys(connection.photos ?? {}).map((photoId) => ({ photoId, itemId: connectionId, itemType: 'connection' })),
      // Get all photos associated with the sections on the connection
      ...Object.entries(connection.sections ?? []).flatMap(([sectionId, section]) =>
        Object.keys(section.photos ?? {}).map((photoId) => ({ photoId, itemId: `${connectionId}:${sectionId}`, itemType: 'section' }))
      )
    ])
  ];

  // Build the update object
  const jobsUpdate = {
    ...rebaseUpdatePaths(jobId, {
      [`nodes/${nodeId}`]: null,
      [`geohash/${nodeId}`]: null,
      [`app_geohash/${nodeId}`]: null,
      ...connectionsToDelete.reduce(
        (connectionsUpdate, [connectionId, connection]) => ({
          ...connectionsUpdate,
          ...composeConnectionDeleteUpdate(connectionId, connection, { jobPhotos, parentNodesDeleted: [nodeId] })
        }),
        {}
      ),
      ...composePhotoUnassociationUpdate(photosToUnassociate, { jobPhotos, itemsDeleted: true })
    }),
    ...(node.reference_data?.job_id && {
      [`${node.reference_data.job_id}/nodes/${nodeId}/reference_data`]: null
    })
  };

  return jobsUpdate;
}

/**
 * Compose an update object to delete a connection from a job. Any sections on the connection will also be deleted. Any photos associated
 * with the connection or sections will be unassociated.
 * @param {string} connectionId - The id of the connection to delete.
 * @param {object} connection - The connection object to delete.
 * @param {object} data - The data needed to compose the update object.
 * @param {Record<string, Object>} data.jobPhotos - The photos associated with the job.
 * @param {string[]} [data.parentNodesDeleted] - The ids of the parent nodes that were deleted. If not provided, the update will include
 * geohash updates for the nodes.
 * @returns {object} The composed update object.
 */
export function composeConnectionDeleteUpdate(connectionId, connection, data) {
  const { jobPhotos, parentNodesDeleted } = data;

  // Get all sections that will be deleted along with the connection
  const sectionsToDelete = Object.entries(connection.sections ?? {});

  // Get all photos that will be unassociated as a result of this delete
  const photosToUnassociate = [
    // Get all photos associated with the connection
    ...Object.keys(connection.photos ?? {}).map((photoId) => ({ photoId, itemId: connectionId, itemType: 'connection' })),
    // Get all photos associated with the sections on the connection
    ...sectionsToDelete.flatMap(([sectionId, section]) =>
      Object.keys(section.photos ?? {}).map((photoId) => ({ photoId, itemId: `${connectionId}:${sectionId}`, itemType: 'section' }))
    )
  ];

  // Build the update object
  const jobUpdate = {
    [`connections/${connectionId}`]: null,
    [`geohash/${connectionId}~1`]: null,
    [`geohash/${connectionId}~2`]: null,
    ...(!parentNodesDeleted?.includes(connection.node_id_1) && {
      [`geohash/${connection.node_id_1}/n/${connectionId}`]: null
    }),
    ...(!parentNodesDeleted?.includes(connection.node_id_2) && {
      [`geohash/${connection.node_id_2}/n/${connectionId}`]: null
    }),
    ...sectionsToDelete.reduce(
      (sectionsUpdate, [sectionKey, section]) => ({
        ...sectionsUpdate,
        ...composeSectionDeleteUpdate(connectionId, sectionKey, section, { jobPhotos, parentConnectionDeleted: true })
      }),
      {}
    ),
    ...composePhotoUnassociationUpdate(photosToUnassociate, { jobPhotos, itemsDeleted: true })
  };

  return jobUpdate;
}

/**
 * Compose an update object to delete a section from a connection. Any photos associated with the section will be unassociated.
 * @param {string} connectionId - The id of the connection that the section belongs to.
 * @param {string} sectionKey - The key of the section to delete.
 * @param {object} section - The section object to delete.
 * @param {object} data - The data needed to compose the update object.
 * @param {Record<string, Object>} data.jobPhotos - The photos associated with the job.
 * @param {boolean} [data.parentConnectionDeleted] - Whether the parent connection was deleted. If true, the update will not include
 * an update to the connection's section list.
 * @returns {object} The composed update object.
 */
export function composeSectionDeleteUpdate(connectionId, sectionKey, section, data) {
  const { jobPhotos, parentConnectionDeleted } = data;

  // Get the unique photo ids from the section
  const photosToUnassociate = Object.keys(section.photos ?? {}).map((photoId) => ({
    photoId,
    itemId: `${connectionId}:${sectionKey}`,
    itemType: 'section'
  }));

  const jobUpdate = {
    ...(!parentConnectionDeleted && {
      [`connections/${connectionId}/sections/${sectionKey}`]: null
    }),
    [`geohash/${connectionId}:${sectionKey}`]: null,
    ...composePhotoUnassociationUpdate(photosToUnassociate, { jobPhotos, itemsDeleted: true })
  };

  return jobUpdate;
}

/**
 * Compose an update object to unassociate multiple photos from nodes, connections, or sections. This function should always be called
 * with the full list of all photo-item associations that need to be broken for an operation. For example, if a node is being deleted,
 * all of the node's photos _and_ all of the photos associated with the node's connections and sections should be passed to this function.
 * @param {import('./KatapultJobUtilities.js').PhotoAssociation[]} photoAssociationsToBreak - A list of photo-item associations to break.
 * @param {Object} data - The data needed to compose the update object.
 * @param {Record<string, Object>} data.jobPhotos - The photos associated with the job.
 * @param {boolean} [data.itemsDeleted] - Whether the items being unassociated are being deleted. If true, the update will not include
 * updates to the item's photo list.
 * @returns {object} The composed update object.
 */
export function composePhotoUnassociationUpdate(photoAssociationsToBreak, data) {
  const { jobPhotos, itemsDeleted } = data;

  // Get the unique photo ids from the unassociations
  const uniquePhotoIds = [...new Set(photoAssociationsToBreak.map(({ photoId }) => photoId))];

  // Get the photo ids that will have no associations after the unassociations
  const photosWithNoAssociations = uniquePhotoIds.filter((uniquePhotoId) => {
    const itemsBeingUnassociated = photoAssociationsToBreak.filter(({ photoId }) => photoId === uniquePhotoId).map(({ itemId }) => itemId);
    const itemsAssociatedToPhoto = Object.keys(jobPhotos[uniquePhotoId]?.associated_locations ?? {});
    return (
      itemsBeingUnassociated.length === itemsAssociatedToPhoto.length &&
      itemsBeingUnassociated.every((itemId) => itemsAssociatedToPhoto.includes(itemId))
    );
  });

  const jobUpdate = {
    // Remove the associations from the photos
    ...Object.fromEntries(
      photoAssociationsToBreak.map(({ itemId, photoId }) => [`photos/${photoId}/associated_locations/${itemId}`, null])
    ),
    // Remove the photos from the items
    ...(!itemsDeleted &&
      Object.fromEntries(
        photoAssociationsToBreak.map(({ itemId, itemType, photoId }) => [
          `${getItemRelativePath(itemId, itemType)}/photos/${photoId}`,
          null
        ])
      )),
    // Update the photo summary for photos with no associations
    ...Object.fromEntries(photosWithNoAssociations.map((photoId) => [`photo_summary/${photoId}/associated`, null]))
    // TODO (2024-04-17): Decrement photo / folder counters? (use `increment` from Firebase)
  };

  return jobUpdate;
}

/**
 * Compose an update object to update a company's permission for a job.
 * @param {string} companyId - The id of the company to update the permission for.
 * @param {string|null} permission - The new permission to give the company (one of `'read'`, `'write'`, `'applicant'`, or `null`).
 * Setting the permission to `null` will un-share the job with the company.
 * @param {object} data - The data needed to compose the update object.
 * @param {string} data.jobId - The id of the job.
 * @param {import('./KatapultJobUtilities').JobDetails} data.jobDetails - The details of the job.
 * @param {Record<string,string>} data.jobSharing - The current sharing object for the job.
 * @param {object} data.metadata - The metadata for the job.
 * @param {object} data.jobChooserOptions - The job chooser options for the company. This can be fetched from
 * `photoheight/company_space/${companyId}/
 * @returns {object} The composed update object.
 */
export function composeCompanyPermissionUpdate(companyId, permission, data) {
  const { jobId, jobDetails, jobSharing, metadata, jobChooserOptions } = data;

  // Get the current permission for the company
  const currentPermission = jobSharing?.[companyId] ?? null;

  // Make the appropriate changes to the job permission data
  const jobPermissionsData =
    // If the company will have a permission, update the job permission data (otherwise delete the record with `null`)
    permission != null
      ? {
          // Always update permission
          permission,
          // If the job is being shared with this company for the first time, add the rest of the job record data
          ...(currentPermission == null && {
            name: jobDetails.name,
            job_creator: jobDetails.model,
            job_owner: jobDetails.owner,
            date_shared: serverTimestamp(),
            metadata: metadata,
            status: 'active',
            published: jobDetails.status?.published ?? null,
            done: jobDetails.done ?? null
          })
        }
      : null;

  // Create an update object and begin updating the permission
  const photoheightUpdate = {
    [`jobs/${jobId}/sharing/${companyId}`]: permission,

    // If the company does not have any permission, share the model space and add the job to the company's project folder
    ...(currentPermission == null && {
      [`company_space/${jobDetails.model}/companies_with_model_access/${companyId}`]: true,
      [`company_space/${companyId}/project_folders/${getFirebaseFolderPath(jobDetails.projectFolder)}/jobs/${jobId}`]: true
    }),

    // If the company has a permission and the new permission is null, remove the job from the company's project folder
    ...(permission == null && {
      [`company_space/${companyId}/project_folders/${getFirebaseFolderPath(jobDetails.projectFolder)}/jobs/${jobId}`]: null
    }),

    // Add the job permissions update
    ...composeJobPermissionsUpdate(jobId, jobPermissionsData, [companyId], { updatePath: 'photoheight', jobChooserOptions })
  };

  return photoheightUpdate;
}

/**
 * Composes an update object for associating a photo with a specific item.
 * @param {string} itemType - The type of the item (e.g., 'node' or 'section').
 * @param {string} itemId - The unique identifier of the item ('nodeId' or 'connectionId:sectionId').
 * @param {string} photoId - The unique identifier of the photo to be associated.
 * @param {'main'|true|null} associationValue - The association type
 * @returns {object|undefined} An object representing the update paths and values for the photo association, or undefined if required parameters are missing.
 */
export function composeItemPhotoAssociationUpdate(itemType, itemId, photoId, associationValue) {
  if (!itemType || !itemId || !photoId || associationValue === undefined)
    throw new Error('Missing required data to associate photo to item');

  // get the item relative path
  const relativePath = getItemRelativePath(itemId, itemType);

  // compose the update object
  const itemAssociationUpdate = {
    [`photos/${photoId}/associated_locations/${itemId}`]: itemType,
    [`${relativePath}/photos/${photoId}`]: { association: associationValue },
    [`photo_summary/${photoId}/associated`]: associationValue != null ? true : null
  };
  return itemAssociationUpdate;
}

/**
 * Composes an update object that changes the association value of a photo that is already associated to an item.
 * Unlike {@link composeItemPhotoAssociationUpdate}, this only writes the `association` leaf, so sibling data on the
 * item's photo record (such as `pre_construction`) is preserved. Promoting a photo to `main` or `old_main` demotes
 * whichever photo currently holds that association.
 * @param {string} itemType - The type of the item (e.g., 'node' or 'section').
 * @param {string} itemId - The unique identifier of the item ('nodeId' or 'connectionId:sectionId').
 * @param {string} photoId - The unique identifier of the photo whose association is changing.
 * @param {'main'|'old_main'|true} associationValue - The new association value.
 * @param {object} itemPhotos - The item's current `photos` collection, used to find the association to demote.
 * @returns {object} An object representing the update paths and values for the association change.
 */
export function composePhotoAssociationValueUpdate(itemType, itemId, photoId, associationValue, itemPhotos) {
  if (!itemType || !itemId || !photoId || associationValue === undefined)
    throw new Error('Missing required data to set a photo association value');

  const relativePath = getItemRelativePath(itemId, itemType);
  const associationValueUpdate = { [`${relativePath}/photos/${photoId}/association`]: associationValue };

  // Only one photo can hold the main or old_main association, so demote whichever photo holds it now.
  if (associationValue === 'main' || associationValue === 'old_main') {
    for (const [otherPhotoId, photo] of Object.entries(itemPhotos ?? {})) {
      if (otherPhotoId === photoId) continue;
      const otherAssociation = typeof photo === 'string' ? photo : photo?.association;
      if (otherAssociation === associationValue) associationValueUpdate[`${relativePath}/photos/${otherPhotoId}/association`] = true;
    }
  }

  return associationValueUpdate;
}

/**
 * Compose an update that writes fields onto an item's photo association record.
 * @param {string} itemType - The type of the item, either 'node' or 'section'.
 * @param {string} itemId - The item id, either 'nodeId' or 'connectionId:sectionId'.
 * @param {string} photoId
 * @param {object} photoData - Fields keyed by paths relative to the item's photo association record.
 * @returns {object} The update keyed by paths relative to the job.
 */
export function composeItemPhotoDataUpdate(itemType, itemId, photoId, photoData) {
  if (!itemType || !itemId || !photoId || !photoData) throw new Error('Missing required data to update item photo data');

  // Prefix each field path with the path of the item's photo association record
  const itemPhotoPath = `${getItemRelativePath(itemId, itemType)}/photos/${photoId}`;
  const photoDataEntries = Object.entries(photoData);
  const jobRelativePhotoDataEntries = photoDataEntries.map(([fieldPath, value]) => [`${itemPhotoPath}/${fieldPath}`, value]);
  return Object.fromEntries(jobRelativePhotoDataEntries);
}
